import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Square,
  RotateCcw,
  Power,
  Thermometer,
  Cpu,
  Zap,
  Fan,
  Activity,
  Edit3,
  Trash2,
  Save,
  Download,
  Terminal as TerminalIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import WebTerminalModal from "@/components/WebTerminalModal";
import {
  formatHashrate,
  formatPower,
  formatUptime,
  tempColor,
  timeAgo,
  efficiency,
} from "@/lib/format";

function MetricRow({ label, value, accent }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[#27272A]/60 py-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
        {label}
      </span>
      <span className={`font-mono text-sm ${accent || "text-[#FAFAFA]"}`}>
        {value}
      </span>
    </div>
  );
}

function GPURow({ gpu }) {
  return (
    <tr
      data-testid={`gpu-row-${gpu.index}`}
      className="border-b border-[#27272A]/50 hover:bg-[#27272A]/30 transition-colors"
    >
      <td className="py-3 px-3 font-mono text-sm text-[#A1A1AA]">#{gpu.index}</td>
      <td className="py-3 px-3 font-body text-sm">{gpu.model || "—"}</td>
      <td className={`py-3 px-3 font-mono text-sm ${tempColor(gpu.temperature)}`}>
        {gpu.temperature?.toFixed(0) ?? 0}°C
      </td>
      <td className="py-3 px-3 font-mono text-sm text-[#10B981]">
        {formatHashrate(gpu.hashrate)}
      </td>
      <td className="py-3 px-3 font-mono text-sm text-[#F59E0B]">
        {formatPower(gpu.power)}
      </td>
      <td className="py-3 px-3 font-mono text-sm text-[#A1A1AA]">
        {gpu.fan_speed?.toFixed(0) ?? 0}%
      </td>
      <td className="py-3 px-3 font-mono text-xs text-[#A1A1AA]">
        {gpu.core_clock || 0}/{gpu.mem_clock || 0}
      </td>
      <td className="py-3 px-3 font-mono text-xs">
        <span className="text-[#10B981]">{gpu.accepted_shares || 0}</span>
        <span className="text-[#A1A1AA]"> / </span>
        <span className="text-[#EF4444]">{gpu.rejected_shares || 0}</span>
      </td>
    </tr>
  );
}

function OverclockPanel({ rig, onSave }) {
  const [oc, setOc] = useState(rig.overclock || { core_offset: 0, mem_offset: 0, core_clock: 0, mem_clock: 0, power_limit: 0, fan_speed: 0 });
  const [saving, setSaving] = useState(false);
  const supported = rig.supported_clocks || {};
  const memSupported = supported.mem || [];
  const coreSupported = supported.core || [];

  useEffect(() => {
    setOc(rig.overclock || { core_offset: 0, mem_offset: 0, core_clock: 0, mem_clock: 0, power_limit: 0, fan_speed: 0 });
  }, [rig.id]);

  const update = (k, v) => setOc((s) => ({ ...s, [k]: Number(v) }));

  const validateClock = (value, supportedList) => {
    if (!value || value === 0) return { ok: true };
    if (!supportedList || supportedList.length === 0) return { ok: true };
    if (supportedList.includes(value)) return { ok: true };
    const closest = supportedList.reduce((p, c) => Math.abs(c - value) < Math.abs(p - value) ? c : p);
    return { ok: false, closest };
  };

  const coreValid = validateClock(oc.core_clock, coreSupported);
  const memValid = validateClock(oc.mem_clock, memSupported);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rigs/${rig.id}`, { overclock: oc });
      if (!coreValid.ok || !memValid.ok) {
        toast.warning("OC inviato — attenzione: alcuni valori potrebbero essere ignorati dalla GPU");
      } else {
        toast.success("Overclock applicato (comando inviato al rig)");
      }
      onSave?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: "core_clock", label: "Core Clock (assoluto)", unit: "MHz (0=def)", min: 0, max: 2500, step: 15, supported: coreSupported, validation: coreValid },
    { key: "mem_clock", label: "Memory Clock (assoluto)", unit: "MHz (0=def)", min: 0, max: 11000, step: 50, supported: memSupported, validation: memValid },
    { key: "core_offset", label: "Core Offset", unit: "MHz", min: -500, max: 500, step: 25 },
    { key: "mem_offset", label: "Memory Offset", unit: "MHz", min: -2000, max: 3000, step: 50 },
    { key: "power_limit", label: "Power Limit", unit: "W (0=def)", min: 0, max: 600, step: 10 },
    { key: "fan_speed", label: "Fan Speed", unit: "% (0=auto)", min: 0, max: 100, step: 5 },
  ];

  return (
    <div className="bento p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            GPU Overclock
          </div>
          <h3 className="font-heading font-bold text-lg mt-1">Profilo OC</h3>
        </div>
        <Cpu className="w-5 h-5 text-[#10B981]" />
      </div>

      <div className="mb-4 bg-[#0B0B0F] border border-[#27272A] rounded-sm p-3 text-[10px] font-mono leading-relaxed text-[#A1A1AA]" data-testid="oc-info-box">
        <div className="text-[#10B981] uppercase tracking-[0.15em] mb-1.5">↻ applicazione hybrid</div>
        <div className="space-y-1">
          <div><span className="text-[#10B981]">●</span> <span className="text-[#FAFAFA]">Power Limit · Core Clock · Mem Clock</span> <span className="text-[#A1A1AA]">→</span> <span className="text-[#10B981]">nvidia-smi · immediato, no restart</span></div>
          <div><span className="text-[#F59E0B]">●</span> <span className="text-[#FAFAFA]">Core Offset · Mem Offset · Fan Speed</span> <span className="text-[#A1A1AA]">→</span> <span className="text-[#F59E0B]">bzminer --oc_* · ~5s restart miner</span></div>
        </div>
      </div>

      <div className="space-y-4">
        {fields.map(({ key, label, unit, min, max, step, supported, validation }) => (
          <div key={key}>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">
                {label}
              </label>
              <span className="font-mono text-sm text-[#FAFAFA]">
                {oc[key]} <span className="text-[#A1A1AA] text-[10px]">{unit}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={min}
                max={max}
                step={step || 25}
                value={oc[key] || 0}
                onChange={(e) => update(key, e.target.value)}
                data-testid={`oc-${key}-slider`}
                className="flex-1 h-1 bg-[#27272A] rounded appearance-none accent-[#10B981]"
              />
              <input
                type="number"
                value={oc[key] || 0}
                onChange={(e) => update(key, e.target.value)}
                data-testid={`oc-${key}-input`}
                className={`w-20 bg-[#0B0B0F] border rounded-sm px-2 py-1 text-sm font-mono text-right outline-none focus:border-[#10B981] ${validation && !validation.ok ? "border-[#EF4444]" : "border-[#27272A]"}`}
              />
            </div>
            {validation && !validation.ok && (
              <div className="mt-1 text-[10px] font-mono text-[#EF4444]">
                ⚠ valore non supportato dalla GPU · usa{" "}
                <button
                  onClick={() => update(key, validation.closest)}
                  className="underline hover:text-[#FAFAFA]"
                >
                  {validation.closest}
                </button>
                {" "}o 0 (auto)
              </div>
            )}
            {supported && supported.length > 0 && (
              <div className="mt-1 text-[10px] font-mono text-[#A1A1AA]">
                supportati: {supported.length > 8
                  ? `${supported.slice(0, 4).join(", ")}, …, ${supported.slice(-3).join(", ")} (${supported.length} valori)`
                  : supported.join(", ")
                }
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="oc-save-btn"
        className="w-full mt-5 bg-[#10B981] hover:bg-[#34D399] disabled:opacity-50 text-[#09090B] font-bold rounded-sm py-2 text-xs uppercase tracking-[0.15em] transition-colors active:scale-[0.98]"
      >
        {saving ? "Applico…" : "Applica Overclock"}
      </button>
    </div>
  );
}

function FlightSheetSelect({ rig, onSave }) {
  const [sheets, setSheets] = useState([]);
  const [selected, setSelected] = useState(rig.flight_sheet_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/flight-sheets").then((r) => setSheets(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rigs/${rig.id}`, { flight_sheet_id: selected || null });
      toast.success("Flight sheet assegnata. Il rig riavvierà il miner.");
      onSave?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bento p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            Flight Sheet
          </div>
          <h3 className="font-heading font-bold text-lg mt-1">Config Miner</h3>
        </div>
        <Activity className="w-5 h-5 text-[#10B981]" />
      </div>
      <select
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        data-testid="flight-sheet-select"
        className="w-full bg-[#0B0B0F] border border-[#27272A] rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-[#10B981]"
      >
        <option value="">— Nessuna —</option>
        {sheets.map((fs) => (
          <option key={fs.id} value={fs.id}>
            {fs.name} ({fs.coin} · {fs.miner})
          </option>
        ))}
      </select>
      {rig.flight_sheet && (
        <div className="mt-3 text-xs font-mono text-[#A1A1AA] space-y-1">
          <div>Pool: <span className="text-[#FAFAFA]">{rig.flight_sheet.pool_url}</span></div>
          <div>Wallet: <span className="text-[#FAFAFA] truncate inline-block max-w-[200px] align-bottom">{rig.flight_sheet.wallet}</span></div>
        </div>
      )}
      <button
        onClick={save}
        disabled={saving}
        data-testid="flight-sheet-save-btn"
        className="w-full mt-4 bg-[#27272A] hover:bg-[#3F3F46] text-[#FAFAFA] rounded-sm py-2 text-xs uppercase tracking-[0.15em] transition-colors active:scale-[0.98]"
      >
        {saving ? "Applico…" : "Applica Flight Sheet"}
      </button>
    </div>
  );
}

export default function RigDetailPage() {
  const { rigId } = useParams();
  const navigate = useNavigate();
  const [rig, setRig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [terminalInitCmd, setTerminalInitCmd] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/rigs/${rigId}`);
      setRig(data);
      setNewName(data.name);
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error("Rig non trovato");
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [rigId]);

  const chartData = useMemo(() => {
    if (!rig?.history) return [];
    return rig.history.map((h) => ({
      time: new Date(h.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
      hash: Number((h.total_hashrate || 0).toFixed(2)),
      temp: Number((h.avg_temp || 0).toFixed(1)),
    }));
  }, [rig]);

  const action = async (act, confirmText) => {
    if (confirmText && !confirm(confirmText)) return;
    try {
      await api.post(`/rigs/${rigId}/control`, { action: act });
      toast.success(`Comando "${act}" inviato`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveName = async () => {
    if (!newName.trim() || newName === rig.name) {
      setRenaming(false);
      return;
    }
    try {
      await api.put(`/rigs/${rigId}`, { name: newName.trim() });
      toast.success("Nome aggiornato");
      setRenaming(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteRig = async () => {
    if (!confirm(`Eliminare definitivamente il rig "${rig.name}"? L'agent dovrà essere reinstallato.`)) return;
    try {
      await api.delete(`/rigs/${rigId}`);
      toast.success("Rig eliminato");
      navigate("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading || !rig) {
    return (
      <div className="p-8 font-mono text-sm text-[#A1A1AA]">Loading rig…</div>
    );
  }

  const online = rig.status === "online";
  const gpus = rig.gpus || [];

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-1.5 text-xs font-mono text-[#A1A1AA] hover:text-[#10B981] mb-3 uppercase tracking-wider"
          >
            <ArrowLeft className="w-3 h-3" /> Tutti i rig
          </Link>
          <div className="flex items-center gap-3">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                online
                  ? "bg-[#10B981] status-pulse shadow-[0_0_10px_rgba(16,185,129,0.6)]"
                  : "bg-[#EF4444] shadow-[0_0_10px_rgba(239,68,68,0.6)]"
              }`}
            />
            {renaming ? (
              <div className="flex items-center gap-2">
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  data-testid="rename-input"
                  className="bg-[#0B0B0F] border border-[#27272A] focus:border-[#10B981] rounded-sm px-2 py-1 font-heading font-bold text-2xl outline-none"
                />
                <button
                  onClick={saveName}
                  data-testid="rename-save"
                  className="text-[#10B981] hover:bg-[#10B981]/10 p-1.5 rounded-sm"
                >
                  <Save className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                  {rig.name}
                </h1>
                <button
                  onClick={() => setRenaming(true)}
                  data-testid="rename-btn"
                  className="text-[#A1A1AA] hover:text-[#FAFAFA] p-1.5"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
          <div className="mt-2 font-mono text-xs text-[#A1A1AA] flex items-center gap-3">
            <span>{rig.hostname}</span>
            <span className="text-[#27272A]">·</span>
            <span>{rig.ip || "—"}</span>
            <span className="text-[#27272A]">·</span>
            <span>agent v{rig.agent_version}</span>
            <span className="text-[#27272A]">·</span>
            <span>last seen {timeAgo(rig.last_seen)}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => { setTerminalInitCmd(""); setTerminalOpen(true); }}
            data-testid="action-terminal"
            className="flex items-center gap-1.5 bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/40 px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
          >
            <TerminalIcon className="w-3.5 h-3.5" /> Terminal
          </button>
          <button
            onClick={() => {
              const miner = (rig?.flight_sheet?.miner || "bzminer").toLowerCase();
              const logPath = {
                bzminer:  "/var/log/bzminer.log",
                lpminer:  "/var/log/lpminer.log",
                srbminer: "/var/log/srbminer.log",
              }[miner] || "/var/log/bzminer.log";
              setTerminalInitCmd(`tail -n 80 -f ${logPath}`);
              setTerminalOpen(true);
            }}
            data-testid="action-miner-log"
            className="flex items-center gap-1.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
            title={`Tail live del log ${rig?.flight_sheet?.miner || "bzminer"}`}
          >
            <Activity className="w-3.5 h-3.5" /> Miner Log
          </button>
          <button
            onClick={() => action("start_miner")}
            data-testid="action-start-miner"
            className="flex items-center gap-1.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
          >
            <Play className="w-3.5 h-3.5" /> Start
          </button>
          <button
            onClick={() => action("stop_miner")}
            data-testid="action-stop-miner"
            className="flex items-center gap-1.5 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/40 px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
          >
            <Square className="w-3.5 h-3.5" /> Stop
          </button>
          <button
            onClick={() => action("restart_miner")}
            data-testid="action-restart-miner"
            className="flex items-center gap-1.5 bg-[#27272A] hover:bg-[#3F3F46] text-[#FAFAFA] border border-[#3F3F46] px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restart
          </button>
          <button
            onClick={() => action("self_update", "Forza self-update dell'agent? Il rig scarica la nuova versione di agent.py e si riavvia.")}
            data-testid="action-self-update"
            className="flex items-center gap-1.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
            title="Pull e applica l'ultima versione di agent.py dal backend"
          >
            <Download className="w-3.5 h-3.5" /> Update
          </button>
          <button
            onClick={() => action("reboot", "Riavviare il rig? Il sistema operativo si riavvierà completamente.")}
            data-testid="action-reboot"
            className="flex items-center gap-1.5 bg-[#EF4444]/10 hover:bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/40 px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
          >
            <Power className="w-3.5 h-3.5" /> Reboot
          </button>
          <button
            onClick={deleteRig}
            data-testid="action-delete-rig"
            className="flex items-center gap-1.5 bg-[#18181B] hover:bg-[#27272A] text-[#A1A1AA] hover:text-[#EF4444] border border-[#27272A] px-3 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-mono"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bento p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            <Activity className="w-3.5 h-3.5" /> Hashrate
          </div>
          <div className="font-mono text-3xl text-[#10B981] mt-2">
            {online ? formatHashrate(rig.total_hashrate) : "—"}
          </div>
          <div className="font-mono text-xs text-[#A1A1AA] mt-1">
            efficiency {online ? efficiency(rig.total_hashrate, rig.total_power) : "—"}
          </div>
        </div>
        <div className="bento p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            <Zap className="w-3.5 h-3.5" /> Power
          </div>
          <div className="font-mono text-3xl text-[#F59E0B] mt-2">
            {online ? formatPower(rig.total_power) : "—"}
          </div>
          <div className="font-mono text-xs text-[#A1A1AA] mt-1">
            {gpus.length} GPU{rig.cpu_power > 0 ? ` + CPU ${rig.cpu_power.toFixed(0)}W` : ""} · uptime {formatUptime(rig.miner_uptime_sec)}
          </div>
        </div>
        <div className="bento p-5" data-testid="cpu-card">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            <Cpu className="w-3.5 h-3.5" /> CPU
          </div>
          <div className="font-mono text-3xl text-[#10B981] mt-2">
            {online && rig.cpu_power > 0 ? `${rig.cpu_power.toFixed(0)}W` : "—"}
          </div>
          <div className="font-mono text-xs text-[#A1A1AA] mt-1">
            {rig.cpu_temp?.toFixed(0) || 0}°C · {(rig.cpu_freq_mhz / 1000)?.toFixed(2) || 0} GHz · {rig.cpu_usage_pct?.toFixed(0) || 0}%
          </div>
        </div>
        <div className="bento p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            <Thermometer className="w-3.5 h-3.5" /> Temp avg/max
          </div>
          <div className="font-mono text-3xl text-[#FAFAFA] mt-2">
            {gpus.length
              ? `${(gpus.reduce((a, g) => a + (g.temperature || 0), 0) / gpus.length).toFixed(0)}° / ${Math.max(...gpus.map((g) => g.temperature || 0)).toFixed(0)}°`
              : "—"}
          </div>
          <div className="font-mono text-xs text-[#A1A1AA] mt-1">CPU {rig.cpu_temp?.toFixed(0) || 0}°C</div>
        </div>
        <div className="bento p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
            <Fan className="w-3.5 h-3.5" /> System
          </div>
          <div className="font-mono text-3xl text-[#FAFAFA] mt-2">
            {rig.ram_used_pct?.toFixed(0) || 0}%
          </div>
          <div className="font-mono text-xs text-[#A1A1AA] mt-1">
            RAM · disk {rig.disk_used_pct?.toFixed(0) || 0}%
          </div>
        </div>
      </div>

      {/* Chart + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bento p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
                Last hour
              </div>
              <h3 className="font-heading font-bold text-lg mt-1">
                Hashrate &amp; temperature
              </h3>
            </div>
          </div>
          <div className="h-64">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#27272A" strokeDasharray="3 3" />
                  <XAxis dataKey="time" stroke="#A1A1AA" fontSize={10} fontFamily="IBM Plex Mono" />
                  <YAxis yAxisId="left" stroke="#10B981" fontSize={10} fontFamily="IBM Plex Mono" />
                  <YAxis yAxisId="right" orientation="right" stroke="#EF4444" fontSize={10} fontFamily="IBM Plex Mono" />
                  <Tooltip
                    contentStyle={{
                      background: "#0B0B0F",
                      border: "1px solid #27272A",
                      borderRadius: 4,
                      fontFamily: "IBM Plex Mono",
                      fontSize: 11,
                    }}
                  />
                  <Line yAxisId="left" type="monotone" dataKey="hash" stroke="#10B981" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="temp" stroke="#EF4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center font-mono text-xs text-[#A1A1AA]">
                Nessun dato storico ancora disponibile
              </div>
            )}
          </div>
        </div>

        <div className="bento p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-3">
            System info
          </div>
          <MetricRow label="Status" value={online ? "ONLINE" : "OFFLINE"} accent={online ? "text-[#10B981]" : "text-[#EF4444]"} />
          <MetricRow label="Miner" value={rig.miner_running ? "RUNNING" : "STOPPED"} accent={rig.miner_running ? "text-[#10B981]" : "text-[#F59E0B]"} />
          <MetricRow label="Hostname" value={rig.hostname} />
          <MetricRow label="IP" value={rig.ip || "—"} />
          <MetricRow label="Agent" value={`v${rig.agent_version}`} />
          <MetricRow label="GPU count" value={rig.gpu_count || gpus.length} />
          <MetricRow label="Last seen" value={timeAgo(rig.last_seen)} />
        </div>
      </div>

      {/* GPUs table */}
      <div className="bento p-5 mb-6 overflow-x-auto">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-3">
          GPUs ({gpus.length})
        </div>
        {gpus.length === 0 ? (
          <div className="py-8 text-center font-mono text-xs text-[#A1A1AA]">
            Nessuna GPU rilevata dall'agent
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-xs font-mono uppercase tracking-wider text-[#A1A1AA] border-b border-[#27272A]">
                <th className="py-2 px-3 text-left">#</th>
                <th className="py-2 px-3 text-left">Model</th>
                <th className="py-2 px-3 text-left">Temp</th>
                <th className="py-2 px-3 text-left">Hashrate</th>
                <th className="py-2 px-3 text-left">Power</th>
                <th className="py-2 px-3 text-left">Fan</th>
                <th className="py-2 px-3 text-left">Clk(core/mem)</th>
                <th className="py-2 px-3 text-left">Shares</th>
              </tr>
            </thead>
            <tbody>
              {gpus.map((g) => <GPURow key={g.index} gpu={g} />)}
            </tbody>
          </table>
        )}
      </div>

      {/* Config panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FlightSheetSelect rig={rig} onSave={load} />
        <OverclockPanel rig={rig} onSave={load} />
      </div>

      <WebTerminalModal
        rigId={rig.id}
        rigName={rig.name}
        open={terminalOpen}
        onClose={() => setTerminalOpen(false)}
        initialCommand={terminalInitCmd}
      />
    </div>
  );
}
